/* @flow */


import { getRandom, apiVersion } from '../utils';
import bidbase from './bidbase';
import adconfig from '../adconfig';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { isJSONSupported, getPageUrl } from '../utils/browserutil';
import { logError } from '../utils/logger';
import { extend, isEmptyObject } from '../utils/coreutils';
import { allSettledPromise } from '../utils/promiseutils';
import { getRTBUserEids } from '../idproviders/idputils';
import { serializeSupplyChain } from '../utils/schainutils';

const END_POINT = 'https://prebid.smilewanted.com';

/**
 * @global
 * @typedef hb_data
 * @property {string} hb_pb Bid value
 * @property {string} hb_bidder Bidder name
 * @property {string} hb_adid Bid ID/creative
 */

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Position Info
 * @property {object} _QP Position Query Param
 * @property {object} _CV Position Custom Vars
 * @property {hb_data} winningBid Winning bid
 */

/**
 * typedef definitions
 */

declare type rtbUid = {|
  id: string,
  atype: number
|};

declare type rtbEid = {|
  source: string,
  uids: Array<rtbUid>
|};

declare type smilewantedReqBody = {|
  bidId: string,
  bidfloor: number,
  currencyCode: string,
  eids: Array<rtbEid>,
  gdpr_consent?: string,
  gdpr?: boolean,
  pageDomain: string,
  prebidVersion: string,
  schain: string | null,
  sizes: Array<{ w: number, h: number }>,
  transactionId: string,
  tagId: string,
  timeout: string,
  zoneId: string
|};

declare type smilewantedBidRequestCache = {|
  impid: {
    [string]: string
  }
|};

declare type smilewantedBidResponseBody = {|
  ad: string,
  cpm: number
|};

class _smilewanted extends bidbase {
  _bidRequest: smilewantedBidRequestCache;

  _config: smilewantedConfig;

  _DefConfig: smilewantedConfig;

  constructor(name: string = 'smilewanted') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      bidUrl: END_POINT,
      zoneId: ''
    });
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['bidUrl', 'zoneId'];
    this._bidRequest = {
      impid: {}
    };
  }

  _buildReqCommonBody(): smilewantedReqBody {
    const { user: { eids } = {} } = getRTBUserEids();
    const { zoneId, timeout } = this._config;
    const version = apiVersion();
    const schainConfigured = this.getSupplyChain();
    // Serialize schain to string format for smilewanted endpoint
    const schain = serializeSupplyChain(schainConfigured, [
      'asi',
      'sid',
      'hp',
      'rid',
      'name',
      'domain',
      'ext'
    ]);

    // build body common for all bid request
    const commonBody = extend(
      true,
      {},
      { eids },
      {
        currencyCode: 'USD',
        pageDomain: getPageUrl(),
        prebidVersion: version,
        schain,
        timeout,
        zoneId
      }
    );
    return commonBody;
  }

  _getBid(pos: string, commonBody: smilewantedReqBody): Promise<boolean> {
    // generate unique ID and cache for response lookup later
    const bidId = getRandom();
    this._bidRequest.impid[bidId] = pos;
    const { sizes = [] } = adconfig.getPosSettings(pos);
    const formattedSizes = sizes.map(([w, h]) => ({ w, h }));
    const posSpecificData = {
      bidId,
      sizes: formattedSizes,
      tagId: pos
    };

    const reqBody = extend(true, {}, commonBody, posSpecificData);
    const req = {
      method: 'POST',
      body: JSON.stringify(reqBody),
      credentials: 'include'
    };

    const opt = {
      data: {}, // query params
      timeout: this._config.timeout,
      logPrefix: `${this.getName()} - ${pos}`
    };

    return fetchJSON(this._config.bidUrl, opt, req).then(
      (json: RTBBidResponse) => {
        this._bidResponses[pos] = json;
        return true;
      }
    );
  }

  /**
   * Builds and sends bid requests
   *
   * @param {Array<string>} posList - Ad positions
   * @returns {Promise} - Returns promise
   * @access private
   */
  _getBids(posList: Array<string>): Array<Promise<boolean>> {
    // get common data for all requests
    const commonBody = this._buildReqCommonBody();

    return posList.map(pos => this._getBid(pos, commonBody));
  }

  /**
   * To-Do(** Need to implement)
   * Process bid response. Response format:
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   */
  _processBidResponse(
    posList: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    posList.forEach(pos => {
      const resp: smilewantedBidResponseBody = this._bidResponses[pos];
      if (!resp || isEmptyObject(resp)) {
        return;
      }

      const { ad = '', cpm = 0 } = resp;
      let impid = '';
      Object.entries(this._bidRequest.impid).forEach(([i, p]) => {
        if (p === pos) {
          impid = i;
        }
      });

      const price = String(cpm);
      if (price.length > 0 && ad.length > 0 && impid.length > 0) {
        this._handleBidPosData(ret, pos, price, impid, ad);
      }
      delete this._bidResponses[pos];
    });
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB Position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};
    this._bidRequest.impid = {};
    // get promise for all bid request
    const pArray = this._getBids(posList);
    return allSettledPromise(pArray).then(
      arr => {
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);
        if (fValues.length) {
          // Log all failed promise errors
          logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      },
      error => {
        logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      }
    );
  }

  /**
   * This method will be called for bidders witout overriden userSync method
   *
   * @external syncObj
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    const ret = [];
    // make sure that syncUrl & zoneId is not empty
    if (this._config.syncUrl.length > 0 && this._config.zoneId.length > 0) {
      ret.push({
        name: this.getName(),
        type: 'iframe',
        url: `${this._config.syncUrl}${this._config.zoneId}`
      });
    }
    return ret;
  }
}

const smilewanted: _smilewanted = new _smilewanted();
export default smilewanted;
